import { useEffect } from 'react';
import { AppState } from 'react-native';
import {
  Easing,
  useFrameCallback,
  useDerivedValue,
  useReducedMotion,
  useSharedValue,
  withSequence,
  withTiming,
  type SharedValue,
} from 'react-native-reanimated';
import { ringWaveSpeed, type MascotBlends } from './nova-mascot-motion';

export type NovaMascotState = 'idle' | 'listening' | 'thinking' | 'speaking' | 'error';

/** The reference's state morph: ease-out-quint-like, never a snap. */
const MORPH = { duration: 420, easing: Easing.bezier(0.16, 1, 0.3, 1) };

export interface MascotDriver {
  /** Seconds, advanced on the UI thread every frame. */
  clock: SharedValue<number>;
  /** The listening ring's wave position in turns; its speed follows the voice. */
  ringPhase: SharedValue<number>;
  blends: Readonly<SharedValue<MascotBlends>>;
  shake: SharedValue<number>;
}

function target(state: NovaMascotState): MascotBlends {
  'worklet';
  return {
    listening: state === 'listening' ? 1 : 0,
    thinking: state === 'thinking' ? 1 : 0,
    speaking: state === 'speaking' ? 1 : 0,
    error: state === 'error' ? 1 : 0,
  };
}

/**
 * The mascot's frame clock and state blends. The clock pauses in the
 * background and under reduced motion (the pose then holds; state changes
 * still land). One shared value holds every blend so a frame reads it once.
 */
export function useMascotDriver(state: NovaMascotState, level: SharedValue<number>): MascotDriver {
  const reduceMotion = useReducedMotion();
  // Start past zero so the first frame isn't mid-blink.
  const clock = useSharedValue(1);
  const ringPhase = useSharedValue(0);
  const from = useSharedValue<MascotBlends>(target(state));
  const to = useSharedValue<MascotBlends>(target(state));
  const progress = useSharedValue(1);
  const blends = useDerivedValue<MascotBlends>(() => {
    const a = from.value;
    const b = to.value;
    const k = progress.value;
    return {
      listening: a.listening + (b.listening - a.listening) * k,
      thinking: a.thinking + (b.thinking - a.thinking) * k,
      speaking: a.speaking + (b.speaking - a.speaking) * k,
      error: a.error + (b.error - a.error) * k,
    };
  });
  const shake = useSharedValue(0);

  const frame = useFrameCallback((info) => {
    const dt = Math.min((info.timeSincePreviousFrame ?? 16) / 1000, 0.064);
    clock.value += dt;
    ringPhase.value += dt * ringWaveSpeed(level.value);
  }, false);

  useEffect(() => {
    const update = (active: boolean) => frame.setActive(active && !reduceMotion);
    update(AppState.currentState === 'active');
    const sub = AppState.addEventListener('change', (next) => update(next === 'active'));
    return () => {
      sub.remove();
      frame.setActive(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceMotion]);

  // Blend from wherever the last morph had got to, so an interrupted change
  // (hold, let go, hold again) continues smoothly instead of jumping.
  useEffect(() => {
    from.value = blends.value;
    to.value = target(state);
    progress.value = 0;
    progress.value = withTiming(1, MORPH);
    if (state === 'error' && !reduceMotion) {
      shake.value = withSequence(
        withTiming(-7, { duration: 60 }),
        withTiming(6, { duration: 80 }),
        withTiming(-4, { duration: 80 }),
        withTiming(2, { duration: 70 }),
        withTiming(0, { duration: 70 }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, reduceMotion]);

  return { clock, ringPhase, blends, shake };
}
